<template>
  <div>
    <sdPageHeader title="TrueLayer Banking">
      <template #subTitle>
        <span class="truelayer-dashboard__subtitle">Sandbox bank statement workspace for TrueLayer balances and recent transaction retrieval.</span>
      </template>
    </sdPageHeader>

    <Main>
      <div class="truelayer-dashboard">
        <TrueLayerConnect
          :loading="state.connecting"
          :disconnecting="state.disconnecting"
          :can-disconnect="Boolean(state.accounts.length)"
          :loading-providers="state.loadingProviders"
          :error="state.connectError"
          :message="state.connectMessage"
          :providers="state.providers"
          :selected-provider-id="state.selectedProviderId"
          :account-count="state.accounts.length"
          :transaction-count="loadedTransactionCount"
          @update:selectedProviderId="state.selectedProviderId = $event"
          @connect="connectBank"
          @disconnect="disconnectBank"
        />

        <a-row :gutter="20" class="truelayer-dashboard__stats">
          <a-col :xs="24" :md="8">
            <div class="truelayer-stat-card">
              <span class="truelayer-stat-card__label">Connected accounts</span>
              <strong>{{ state.accounts.length }}</strong>
              <p>Accounts currently stored from successful TrueLayer callbacks.</p>
            </div>
          </a-col>
          <a-col :xs="24" :md="8">
            <div class="truelayer-stat-card">
              <span class="truelayer-stat-card__label">Selected provider</span>
              <strong>{{ selectedProviderLabel }}</strong>
              <p>{{ selectedAccountLabel }}</p>
            </div>
          </a-col>
          <a-col :xs="24" :md="8">
            <div class="truelayer-stat-card truelayer-stat-card--accent">
              <span class="truelayer-stat-card__label">Transactions loaded</span>
              <strong>{{ loadedTransactionCount }}</strong>
              <p>Statement lines saved for the last 90 days.</p>
            </div>
          </a-col>
        </a-row>

        <a-row :gutter="24">
          <a-col :xs="24" :xl="10">
            <BankList
              :accounts="state.accounts"
              :loading="state.loadingAccounts"
              :selected-account-id="state.selectedAccount ? state.selectedAccount.id : null"
              @select="selectAccount"
            />
          </a-col>
          <a-col :xs="24" :xl="14">
            <Transactions
              :account="state.selectedAccount"
              :transactions="state.transactions"
              :loading="state.loadingTransactions"
              :error="state.transactionError"
            />
          </a-col>
        </a-row>
      </div>
    </Main>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Main } from '../styled';
import TrueLayerConnect from '@/components/banking/TrueLayerConnect.vue';
import BankList from '@/components/banking/BankList.vue';
import Transactions from '@/components/banking/Transactions.vue';
import TrueLayerService from '@/services/truelayer.service';

export default defineComponent({
  name: 'TrueLayerDashboard',
  components: {
    Main,
    TrueLayerConnect,
    BankList,
    Transactions,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const state = reactive({
      connecting: false,
      disconnecting: false,
      loadingProviders: false,
      loadingAccounts: false,
      loadingTransactions: false,
      connectError: '',
      connectMessage: '',
      transactionError: '',
      providers: [],
      selectedProviderId: '',
      accounts: [],
      transactions: [],
      selectedAccount: null,
    });

    const loadedTransactionCount = computed(() => state.transactions.length);

    const selectedAccountLabel = computed(() => {
      if (!state.selectedAccount) {
        return 'No account selected';
      }

      return state.selectedAccount.account_name || state.selectedAccount.name || 'Unnamed account';
    });

    const selectedProviderLabel = computed(() => {
      const provider = state.providers.find((item) => item.provider_id === state.selectedProviderId);

      if (provider) {
        return provider.display_name || provider.provider_id;
      }

      if (state.selectedAccount) {
        return state.selectedAccount.institution_name || state.selectedAccount.institution_id || 'Connected provider';
      }

      return 'No provider selected';
    });

    const loadBanks = async () => {
      state.loadingAccounts = true;

      try {
        const response = await TrueLayerService.getBanks();
        state.accounts = response.data.data || [];

        if (!state.accounts.length) {
          state.selectedAccount = null;
          state.transactions = [];
          return;
        }

        if (!state.selectedAccount && state.accounts.length) {
          await selectAccount(state.accounts[0]);
        }
      } catch (error) {
        state.connectError = error?.response?.data?.message || 'Unable to load connected TrueLayer bank accounts.';
      } finally {
        state.loadingAccounts = false;
      }
    };

    const loadProviders = async () => {
      state.loadingProviders = true;

      try {
        const response = await TrueLayerService.getProviders('uk');
        state.providers = response.data.data || [];

        if (!state.selectedProviderId && state.providers.length) {
          state.selectedProviderId = state.providers[0].provider_id;
        }
      } catch (error) {
        state.connectError = error?.response?.data?.message || 'Unable to load available TrueLayer providers.';
      } finally {
        state.loadingProviders = false;
      }
    };

    const loadTransactions = async (account) => {
      if (!account) {
        state.transactions = [];
        return;
      }

      state.loadingTransactions = true;
      state.transactionError = '';

      try {
        const response = await TrueLayerService.getTransactions(account.id || account.account_id);
        state.selectedAccount = response.data.account || account;
        state.transactions = response.data.data || [];
      } catch (error) {
        state.transactionError = error?.response?.data?.message || 'Unable to load transactions for the selected account.';
      } finally {
        state.loadingTransactions = false;
      }
    };

    const selectAccount = async (account) => {
      state.selectedAccount = account;
      await loadTransactions(account);
    };

    const resetConnectedData = () => {
      state.accounts = [];
      state.transactions = [];
      state.selectedAccount = null;
      state.transactionError = '';
    };

    const connectBank = async () => {
      state.connecting = true;
      state.connectError = '';
      state.connectMessage = '';

      try {
        const response = await TrueLayerService.connectBank({
          country_code: 'uk',
          provider_id: state.selectedProviderId || undefined,
        });
        const redirectUrl = response.data.redirect_url;

        if (!redirectUrl) {
          throw new Error(response.data.message || 'TrueLayer did not return an authorisation URL.');
        }

        window.location.assign(redirectUrl);
      } catch (error) {
        state.connectError = error?.response?.data?.message || error.message || 'Unable to create a TrueLayer connect session.';
        state.connecting = false;
      }
    };

    const disconnectBank = async () => {
      if (!state.accounts.length) {
        return;
      }

      const confirmed = window.confirm('Disconnect TrueLayer and clear the stored TrueLayer accounts, transactions, and tokens for this user?');

      if (!confirmed) {
        return;
      }

      state.disconnecting = true;
      state.connectError = '';
      state.connectMessage = '';

      try {
        const response = await TrueLayerService.disconnectBank();
        const warnings = response?.data?.warnings || [];

        resetConnectedData();
        await loadBanks();
        state.connectMessage = warnings.length
          ? `${response.data.message} ${warnings.join(' ')}`
          : response.data.message;
      } catch (error) {
        state.connectError = error?.response?.data?.message || 'Unable to disconnect TrueLayer accounts.';
      } finally {
        state.disconnecting = false;
      }
    };

    const hydrateFromCallback = async () => {
      const { status, message, accounts, transactions } = route.query;

      if (status === 'success') {
        state.connectError = '';
        state.connectMessage = `TrueLayer connection complete${accounts ? `: ${accounts} account(s)` : ''}${transactions ? ` and ${transactions} transaction(s)` : ''} synced.`;
        await loadBanks();
      }

      if (status === 'error') {
        state.connectMessage = '';
        state.connectError = message || 'The TrueLayer callback returned an error.';
      }

      if (route.name === 'truelayer-callback') {
        router.replace({ name: 'truelayer' });
      }
    };

    onMounted(async () => {
      await loadProviders();
      await loadBanks();
      await hydrateFromCallback();
    });

    watch(
      () => route.fullPath,
      async () => {
        await hydrateFromCallback();
      },
    );

    return {
      state,
      connectBank,
      disconnectBank,
      loadedTransactionCount,
      selectedAccountLabel,
      selectedProviderLabel,
      selectAccount,
    };
  },
});
</script>

<style scoped>
.truelayer-dashboard {
  display: grid;
  gap: 24px;
  position: relative;
}

.truelayer-dashboard::before {
  content: '';
  position: absolute;
  inset: 18px 0 auto 0;
  height: 240px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 38%),
    radial-gradient(circle at top right, rgba(34, 197, 94, 0.12), transparent 42%),
    linear-gradient(180deg, #f6fbff 0%, rgba(246, 251, 255, 0) 100%);
  pointer-events: none;
  z-index: 0;
}

.truelayer-dashboard > * {
  position: relative;
  z-index: 1;
}

.truelayer-dashboard__subtitle {
  color: #526072;
}

.truelayer-dashboard__stats {
  margin-top: -4px;
}

.truelayer-stat-card {
  min-height: 148px;
  padding: 22px 24px;
  border: 1px solid #d9e4ef;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(10px);
}

.truelayer-stat-card--accent {
  border-color: rgba(14, 165, 233, 0.22);
  background: linear-gradient(180deg, rgba(239, 248, 255, 0.96) 0%, rgba(255, 255, 255, 0.96) 100%);
}

.truelayer-stat-card__label {
  display: inline-block;
  margin-bottom: 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.truelayer-stat-card strong {
  display: block;
  margin-bottom: 10px;
  font-size: 28px;
  line-height: 1.15;
  color: #0f172a;
}

.truelayer-stat-card p {
  margin: 0;
  color: #5f6f82;
  line-height: 1.55;
}

@media only screen and (max-width: 767px) {
  .truelayer-stat-card {
    min-height: auto;
  }
}
</style>